// Light / dark theme: one header button that switches to the other scheme, following the OS
// until the person picks one that differs from it. Colours are CSS custom properties on
// :root; styles.css applies the dark palette under `prefers-color-scheme: dark` unless
// `data-theme="light"` pins light, and under `data-theme="dark"` whatever the OS says. The choice
// is a UI preference only, kept in localStorage behind try/catch (storage can be blocked), and
// public/theme.js applies it before the first paint under the same key.

export type ThemeChoice = "system" | "light" | "dark";

export const THEME_STORAGE_KEY = "nexus-admin-theme";

export type Scheme = "light" | "dark";

/** The scheme the page shows: the pinned one, or the OS's when following the system. */
export function effectiveScheme(choice: ThemeChoice, osDark: boolean): Scheme {
  return choice === "system" ? (osDark ? "dark" : "light") : choice;
}

/**
 * The choice after one click: the other scheme, pinned; or "system" when that scheme is what the
 * OS shows anyway, so the app goes back to following the OS.
 */
export function toggleTheme(choice: ThemeChoice, osDark: boolean): ThemeChoice {
  const next: Scheme = effectiveScheme(choice, osDark) === "dark" ? "light" : "dark";
  return next === (osDark ? "dark" : "light") ? "system" : next;
}

/** The toggle's accessible name and tooltip, and its icon: what a click switches to. */
export function toggleView(shown: Scheme): { label: string; icon: "sun" | "moon" } {
  return shown === "dark" ? { label: "Switch to light theme", icon: "sun" } : { label: "Switch to dark theme", icon: "moon" };
}

/** The subset of Storage the theme needs, so tests can pass a fake or a throwing one. */
export interface ThemeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function parseTheme(value: string | null | undefined): ThemeChoice {
  return value === "light" || value === "dark" ? value : "system";
}

/** The remembered choice; "system" when nothing is stored or storage is unavailable. */
export function readTheme(storage: ThemeStorage | null): ThemeChoice {
  try {
    return parseTheme(storage?.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
}

/** Remember the choice ("system" forgets it). Storage failures are ignored: the choice still applies. */
export function writeTheme(storage: ThemeStorage | null, choice: ThemeChoice): void {
  try {
    if (choice === "system") storage?.removeItem(THEME_STORAGE_KEY);
    else storage?.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Private windows and blocked site data: the theme lasts for this page only.
  }
}

/** The subset of an element the theme touches (document.documentElement in the app). */
export interface ThemeRoot {
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

/** Pin the page to light or dark, or let it follow the OS again. */
export function applyTheme(root: ThemeRoot, choice: ThemeChoice): void {
  if (choice === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", choice);
}

/** The browser's localStorage, or null where touching it throws. */
export function browserStorage(scope: { localStorage?: ThemeStorage } = globalThis as { localStorage?: ThemeStorage }): ThemeStorage | null {
  try {
    return scope.localStorage ?? null;
  } catch {
    return null;
  }
}
